import { useCallback, useEffect, useRef, useState } from 'react';
import type { CrucibleFlavorPreview, VeilSaturationId } from '@sorrem/shared';
import { useLazyGetCrucibleFlavorQuery } from '@sorrem-client/ui/store';

export interface CrucibleFlavorPreviewState {
    /** The roll the form would create, or null while there is none (not random, or no flavor content). */
    readonly preview: CrucibleFlavorPreview | null;
    /** A new roll: another place, feeling and name. */
    readonly reroll: () => void;
}

/**
 * A random crucible's rolled name, backstory, place and saturation for the
 * create form (#225). The roll keeps its seed while the size or saturation
 * changes, so only what those decide changes; `reroll` asks for a new seed.
 * Creating with `preview.flavorSeed` makes exactly the previewed crucible.
 */
export function useCrucibleFlavorPreview(recipeId: string | null, saturation: VeilSaturationId | null): CrucibleFlavorPreviewState {
    // Each ask reads afresh (no cached roll): without a seed, every read is a new roll.
    const [previewFlavor] = useLazyGetCrucibleFlavorQuery();
    const [preview, setPreview] = useState<CrucibleFlavorPreview | null>(null);
    const [rollCount, setRollCount] = useState(0);
    // Read when asking, not a dependency: storing the returned seed must not ask again.
    const seedRef = useRef<string | null>(null);

    useEffect(() => {
        if (recipeId === null) {
            setPreview(null);
            return undefined;
        }
        let isCurrent = true;
        const seed = seedRef.current;
        previewFlavor({ recipeId, ...(saturation ? { veilSaturation: saturation } : {}), ...(seed ? { flavorSeed: seed } : {}) })
            .unwrap()
            .then((result) => {
                if (isCurrent) {
                    seedRef.current = result.flavorSeed;
                    setPreview(result);
                }
            })
            .catch(() => {
                // No flavor (no content, or offline): the crucible keeps its size's name.
                if (isCurrent) {
                    setPreview(null);
                }
            });
        return () => {
            isCurrent = false;
        };
    }, [previewFlavor, recipeId, saturation, rollCount]);

    const reroll = useCallback((): void => {
        seedRef.current = null;
        setRollCount((count) => count + 1);
    }, []);

    return { preview, reroll };
}
